import {DropZone} from "@/components/ui/forms/DropZone";
import {FormError} from "@/components/ui/forms/FormError";
import {setError} from "@/redux/slices/commonSlices/requestSlice";
import {resetUpload, uploadFile} from "@/redux/slices/commonSlices/uploadSlice";

import {useCallback, useEffect, useRef, useState} from "react";

import downloadImage from "@images/popups/download.svg";
import {usePopup} from "@layouts/popup/InfoPopup/PopupContext";
import {SuccessfulSavePopup} from "@popups/attentionPopups/SuccessfulSavePopup";
import {RootButton} from "@ui/buttons/RootButton";
import {RootDesc} from "@ui/descriptions/RootDesc";
import {SmallDesc} from "@ui/descriptions/SmallDesc";
import {RootIcon} from "@ui/icons/RootIcon";
import {H3} from "@ui/titles/H3";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";

import styles from "./styles.module.scss";

const UploadSection = ({
  title,
  description,
  accept,
  type,
  buttonText,
  successTitle,
}) => {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const {openPopup} = usePopup();
  const [file, setFile] = useState(null);
  const controllerRef = useRef(null);

  const {progress, uploading, error} = useSelector(
    (state) => state.common.upload,
  );

  const uploadFileHandler = useCallback((file) => {
    if (file) {
      setFile(file);
      dispatch(resetUpload());
    } else {
      console.error("File is undefined!");
    }
  }, []);

  const handleClickCancel = () => {
    if (controllerRef.current) {
      controllerRef.current.abort();

      dispatch(resetUpload());
      setFile(null);
    } else return;
  };

  const handleSendFile = async () => {
    try {
      if (file) {
        controllerRef.current = new AbortController();

        const resultAction = await dispatch(
          uploadFile({file, controller: controllerRef.current}),
        );

        if (uploadFile.fulfilled.match(resultAction)) {
          dispatch(resetUpload());
          setFile(null);

          openPopup(<SuccessfulSavePopup title={successTitle} />);
        }
      }
    } catch (error) {
      console.error("Error uploading file: ", error);

      dispatch(setError(error.message));
      dispatch(resetUpload());
      setFile(null);
    }
  };

  useEffect(() => {
    return () => {
      dispatch(resetUpload());
      setFile(null);
    };
  }, []);

  return (
    <div className={styles.upload}>
      <H3>
        <span dangerouslySetInnerHTML={{__html: title}} />
      </H3>

      <div className={styles.uploadDesc}>{description}</div>

      <DropZone
        maxFiles={1}
        accept={accept}
        onUpload={uploadFileHandler}
        size={5 * 1024 * 1024}
        type={type}
      />

      {file && (
        <div className={styles.uploadInfo}>
          <div className={styles.fileInfo}>
            {uploading && (
              <div
                style={progress === 100 ? {animation: "none"} : {}}
                className={styles.spinner}
              >
                {error ? (
                  <RootIcon
                    width={16}
                    height={16}
                    id={"error-upload"}
                  />
                ) : (
                  <RootIcon
                    width={16}
                    height={16}
                    id={progress === 100 ? "success-upload" : "spinner-upload"}
                  />
                )}
              </div>
            )}

            <div className={styles.fileContent}>
              <div key={file?.name}>
                <SmallDesc>
                  <span>{`${file?.name} - ${Math.round(
                    file?.size / (1024 * 1024),
                  )}MB`}</span>
                </SmallDesc>
              </div>

              {uploading && (
                <div className={styles.progressBarWrapper}>
                  <div className={styles.progressBar}>
                    <i
                      style={{
                        background: error ? `var(--red)` : `var(--violet)`,
                        width: `${progress}%`,
                      }}
                    />
                  </div>

                  {progress !== 100 && (
                    <div onClick={handleClickCancel}>
                      <RootIcon
                        width={16}
                        height={16}
                        id={"cancel-upload"}
                      />
                    </div>
                  )}

                  {progress === 100 && error && (
                    <div onClick={handleClickCancel}>
                      <RootIcon
                        width={16}
                        height={16}
                        id={"refresh-upload"}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {error && (
            <FormError
              error={error || t("server_error.uploading")}
              bottom={100}
              center
            />
          )}

          <RootButton
            onClick={handleSendFile}
            txt={buttonText}
            disabled={uploading}
          />
        </div>
      )}
    </div>
  );
};

export const UploadFileProgressPopup = ({type}) => {
  const sampleFileRu = "/documents/sample_users_ru.csv";
  const sampleFileEu = "/documents/sample_users_eu.csv";

  const {language} = useSelector((state) => state.common.settings);

  const {t} = useTranslation();

  if (type === "csv") {
    return (
      <UploadSection
        title={t("popup.upload_users.title")}
        buttonText={t("button.save_file")}
        accept={{"text/csv": [".csv"]}}
        type={"csv"}
        successTitle={t("popup.upload_users.success_title")}
        description={
          <ol>
            <li>
              <RootDesc>
                <span
                  dangerouslySetInnerHTML={{
                    __html: t("popup.upload_users.first_step"),
                  }}
                />
                <br />
                <a
                  href={language === "ru" ? sampleFileRu : sampleFileEu}
                  download
                >
                  <img
                    src={downloadImage}
                    alt={"download"}
                  />
                  <span className={"text-[var(--grey)]"}>template.csv</span>
                </a>
                <br />
                <br />
              </RootDesc>
            </li>

            <li>
              <RootDesc>
                <span
                  dangerouslySetInnerHTML={{
                    __html: t("popup.upload_users.second_step"),
                  }}
                />
              </RootDesc>
            </li>

            <li>
              <RootDesc>
                <span
                  dangerouslySetInnerHTML={{
                    __html: t("popup.upload_users.third_step"),
                  }}
                />
              </RootDesc>
            </li>

            <li>
              <RootDesc>
                <span
                  dangerouslySetInnerHTML={{
                    __html: t("popup.upload_users.fourth_step"),
                  }}
                />
              </RootDesc>
            </li>
          </ol>
        }
      />
    );
  } else {
    return (
      <UploadSection
        title={t("popup.profile_photo.title")}
        buttonText={t("button.save_image")}
        accept={{"image/*": [".png", ".jpg"]}}
        type={"jpg, png"}
        description={
          <RootDesc>
            <span
              dangerouslySetInnerHTML={{
                __html: t("popup.profile_photo.subtitle"),
              }}
            />
          </RootDesc>
        }
      />
    );
  }
};
